/**
 * CreateFolderModal · 新建文件夹 / 子文件夹弹窗（通用）
 *
 * 比 photoFolders 老页面的"先创建未命名再去详情改名"多一步：本弹窗让用户直接
 * 在创建前输入名字 · 这样命名义务前置，避免列表里短期出现"未命名文件夹"。
 *
 * 同时支持两种使用场景：
 *  · parentFolderID=0（默认）→ 顶层文件夹；成功后默认跳新详情页
 *  · parentFolderID=<父 folderID> → 子文件夹；调用方可传 onCreated 拦截后续动作
 *    （如父详情页希望"创建后留在原页面，仅刷新子文件夹列表"）
 *
 * 流程：
 *  · 用户输入名字 + Enter / 点确认
 *  · 校验：去空格、emoji 剔除（与老页面 raplaceEmoji 同款）、长度 ≤ 60 字符
 *    （汉字 2 字符）
 *  · dispatch createFolder({groupID, folderName, parentFolderID, source})
 *  · 成功 → 调 onCreated（未传则走默认：bumpFolderRefreshTick + 跳新详情）
 *  · 失败 → message.error 兜底，弹窗保持打开让用户改 / 重试
 *
 * source 用于后端埋点区分入口：
 *  · 5 = AI 分类列表页新建（顶层）
 *  · 6 = AI 分类详情页新建子文件夹（与老 photoFolders/Detail source=6 对齐）
 */

import React, { useCallback, useEffect, useState } from 'react'
import { Input, Modal, message } from 'antd'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../../../app/hooks'
import { createFolder } from '../../../../actions/folder'
import { bumpFolderRefreshTick } from '../../../../slices/aiClassify'
import {
  calcCharactersLen,
  raplaceEmoji as replaceEmoji
} from '../../../../utils/utils'
import { trackClassifyClick } from '../../utils/trackClassify'

export interface CreateFolderModalProps {
  visible: boolean
  onClose: () => void
  /** 父文件夹 ID · 默认 0（顶层）；详情页新建子文件夹场景传当前 folderID */
  parentFolderID?: number
  /**
   * 自定义弹窗标题 · 默认"新建文件夹"
   * 子文件夹场景调用方传"新建子文件夹"，让用户更明确当前创建的层级
   */
  title?: string
  /**
   * 创建成功回调 · 不传则走默认行为：bumpFolderRefreshTick + 跳新详情
   *
   * 父详情页新建子文件夹时建议传自定义 onCreated：
   *  · 自己刷新子文件夹列表（避免父页面 state 失同步）
   *  · 不跳详情页（让用户继续在父页面里操作）
   */
  onCreated?: (createdFolderID: string | number) => void
}

/** 文件夹名最大字符数 · 与 photoFolders/Detail 现有 60 限制一致（汉字算 2） */
const MAX_FOLDER_NAME_LEN = 60

const CreateFolderModal: React.FC<CreateFolderModalProps> = ({
  visible,
  onClose,
  parentFolderID = 0,
  title = '新建文件夹',
  onCreated
}) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const groupID = useAppSelector((s: any) => s.group.groupId)

  const [name, setName] = useState('')
  const [submitting, setSubmitting] = useState(false)

  // 每次打开弹窗清空输入
  useEffect(() => {
    if (visible) setName('')
  }, [visible])

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    // 即时剔除 emoji（与老页面 FolderName 行为一致）
    const cleaned = replaceEmoji(e.target.value)
    // 超过 60 字符不再追加，但已有内容保留
    if (calcCharactersLen(cleaned, MAX_FOLDER_NAME_LEN)) {
      setName(cleaned)
    }
  }, [])

  const handleConfirm = useCallback(() => {
    const trimmed = name.trim()
    if (!trimmed) {
      message.warning('文件夹名称不能为空')
      return
    }
    if (!groupID) {
      message.error('未获取到团队 ID，无法创建')
      return
    }
    setSubmitting(true)
    // source 区分入口：parentFolderID=0 顶层（5），其它走详情页子文件夹（6）
    const sourceFlag = parentFolderID > 0 ? 6 : 5
    Promise.resolve(
      dispatch(
        createFolder({
          groupID,
          folderName: trimmed,
          parentFolderID,
          source: sourceFlag
        })
      )
    )
      .then((data: { folderID?: string | number } | undefined) => {
        if (!data || !data.folderID) {
          message.error('创建失败，请稍后重试')
          return
        }
        if (parentFolderID === 0) {
          trackClassifyClick('confirmCreate', { folderName: trimmed })
        }
        message.success(parentFolderID > 0 ? '已创建子文件夹' : '已创建文件夹')
        onClose()
        // 跨设备同步建夹通知 (原 recordManualFolderCreateTask) 已随
        // 「文件夹创建任务列表」业务一起下线。本端 toast 已经反馈, 不再写后端记录。
        if (onCreated) {
          // 调用方完全接管后续动作（不刷新列表 / 不跳转 / 自己决定）
          onCreated(data.folderID)
        } else {
          // 默认：列表重拉 + 跳新详情
          dispatch(bumpFolderRefreshTick({ newIds: [String(data.folderID)] }))
          navigate(`/ai-classify/folder/${data.folderID}`)
        }
      })
      .catch(() => {
        message.error('创建失败，请稍后重试')
      })
      .finally(() => {
        setSubmitting(false)
      })
  }, [name, groupID, dispatch, onClose, parentFolderID, onCreated])

  return (
    <Modal
      open={visible}
      title={title}
      okText="创建"
      cancelText="取消"
      confirmLoading={submitting}
      onOk={handleConfirm}
      onCancel={onClose}
      destroyOnHidden
      mask={{ closable: !submitting }}
      closable={!submitting}
    >
      <Input
        value={name}
        onChange={handleChange}
        placeholder="请输入文件夹名称"
        onPressEnter={handleConfirm}
        autoFocus
      />
    </Modal>
  )
}

export default CreateFolderModal
